import { useState } from 'react';
import {
    ChevronRight, Crosshair, MapPin, Calendar, Gamepad2, BarChart3,
    Trophy, HardDrive, Users, Plus, KeyRound, Sparkles, ArrowRight,
    AlertTriangle, Globe, Check
} from 'lucide-react';
import './WelcomeWizard.css';
import { cn } from '../lib/utils';
import axios from 'axios';
import { WelcomeSteamStep } from './WelcomeSteamStep';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const SERVER_LOCATIONS = [
    { id: 'europe-west', flag: '🇩🇪', city: 'Frankfurt', region: 'EU West', cc: 'de' },
    { id: 'europe-north', flag: '🇸🇪', city: 'Stockholm', region: 'EU North', cc: 'se' },
    { id: 'europe-east', flag: '🇵🇱', city: 'Warsaw', region: 'EU East', cc: 'pl' },
    { id: 'europe-france', flag: '🇫🇷', city: 'Paris', region: 'EU West', cc: 'fr' },
    { id: 'europe-uk', flag: '🇬🇧', city: 'London', region: 'EU West', cc: 'gb' },
    { id: 'middle-east', flag: '🇦🇪', city: 'Dubai', region: 'Middle East', cc: 'ae' },
    { id: 'north-america-east', flag: '🇺🇸', city: 'New York', region: 'NA East', cc: 'us' },
    { id: 'north-america-west', flag: '🇺🇸', city: 'Los Angeles', region: 'NA West', cc: 'us' },
];

interface WelcomeWizardProps {
    onComplete: () => void;
    faceitTeams: any[];
    loadingFaceitTeams: boolean;
    noFaceitAccount: boolean;
    user: any;
    onTeamAction: (teamId: string, action: 'create' | 'join', team?: any, serverLocation?: string) => Promise<void>;
    creatingTeamId: string | null;
    teamActionError: string;
}

const FEATURES = [
    {
        icon: <Crosshair size={18} />,
        color: 'rgba(99, 102, 241, 0.15)',
        iconColor: '#818cf8',
        title: 'Demo Analysis',
        desc: 'Upload & analyze CS2 demos with detailed stats and demo replays',
    },
    {
        icon: <MapPin size={18} />,
        color: 'rgba(168, 85, 247, 0.15)',
        iconColor: '#a78bfa',
        title: 'Strategy Board',
        desc: 'Record and manage tactics with Dry Run servers and replays',
    },
    {
        icon: <HardDrive size={18} />,
        color: 'rgba(16, 185, 129, 0.15)',
        iconColor: '#34d399',
        title: 'Demo Storage',
        desc: 'Store match demos long-term and access them anytime',
    },
    {
        icon: <Trophy size={18} />,
        color: 'rgba(245, 158, 11, 0.15)',
        iconColor: '#fbbf24',
        title: 'League Tracking',
        desc: 'Track your leagues, seasons, and match results',
    },
    {
        icon: <Calendar size={18} />,
        color: 'rgba(236, 72, 153, 0.15)',
        iconColor: '#f472b6',
        title: 'Practice Scheduling',
        desc: 'Coordinate team availability and plan practice sessions',
    },
    {
        icon: <BarChart3 size={18} />,
        color: 'rgba(6, 182, 212, 0.15)',
        iconColor: '#22d3ee',
        title: 'Player Stats',
        desc: 'Track performance, K/D, ADR, and more over time',
    },
    {
        icon: <Gamepad2 size={18} />,
        color: 'rgba(239, 68, 68, 0.15)',
        iconColor: '#f87171',
        title: 'Game Modes',
        desc: 'Sharpen your aim in Deathmatch, Prefire, Crossfire and Peak Fire',
    },
    {
        icon: <Crosshair size={18} />,
        color: 'rgba(34, 197, 94, 0.15)',
        iconColor: '#4ade80',
        title: 'Grenade Lineups',
        desc: 'Save, share, and manage grenade lineups on interactive maps',
    },
    {
        icon: <Users size={18} />,
        color: 'rgba(251, 146, 60, 0.15)',
        iconColor: '#fb923c',
        title: 'Scrims & Roster',
        desc: 'Find scrim partners, manage your roster, and track members',
    },
];

export function WelcomeWizard({ onComplete, faceitTeams, loadingFaceitTeams, noFaceitAccount, user, onTeamAction, creatingTeamId, teamActionError }: WelcomeWizardProps) {
    const [step, setStep] = useState(0);
    const [exiting, setExiting] = useState(false);
    const [slideKey, setSlideKey] = useState(0);
    const [selectedLocation, setSelectedLocation] = useState((user as any)?.serverLocation || 'europe-west');

    const totalSteps = 5;

    const handleNext = async () => {
        if (step === 1) {
            try {
                await axios.patch(`${API}/users/me`, { serverLocation: selectedLocation });
            } catch (err) {
                console.error('Failed to save server location', err);
            }
        }

        if (step < totalSteps - 1) {
            setStep(s => s + 1);
            setSlideKey(k => k + 1);
        }
    };

    const handleSkip = () => {
        setExiting(true);
        setTimeout(() => onComplete(), 400);
    };

    return (
        <div className={`wizard-overlay ${exiting ? 'wizard-exiting' : ''}`}>
            <div className={`wizard-card ${step === 0 ? 'wizard-card-welcome' : ''}`}>
                {step === 0 && <div className="wizard-hero" aria-hidden="true"><video src="/videos/heros-bg-looped.mp4" autoPlay muted loop playsInline preload="metadata" /><div /></div>}
                <button className="wizard-skip" onClick={handleSkip}>
                    Skip
                </button>

                <div className="wizard-content">
                    {step === 0 && (
                        <div className="wizard-slide" key={slideKey}>
                            {/* Logo */}
                            <div className="wizard-logo">
                                <img src="/logo.png" alt="StratSync" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                <span className="wizard-logo-text">Welcome to StratSync</span>
                            </div>

                            <p className="wizard-subtitle" style={{ maxWidth: '520px', margin: '0 auto 28px' }}>
                                Your competitive CS2 hub. Train your aim, climb the leaderboards and build a stronger team.
                            </p>

                            {/* Feature Grid — read-only showcase */}
                            <div className="wizard-features">
                                {FEATURES.map((f) => (
                                    <div
                                        className="wizard-feature"
                                        key={f.title}
                                        style={{ cursor: 'default' }}
                                    >
                                        <div
                                            className="wizard-feature-icon"
                                            style={{ background: f.color, color: f.iconColor }}
                                        >
                                            {f.icon}
                                        </div>
                                        <div className="wizard-feature-text">
                                            <h4>{f.title}</h4>
                                            <p>{f.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <p style={{ textAlign: 'center', color: '#64748b', fontSize: '12px', marginTop: '16px', fontStyle: 'italic' }}>
                                ...and much more. Let's get you set up!
                            </p>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="wizard-slide" key={slideKey}>
                            <h2 className="wizard-team-title" style={{ marginBottom: 8 }}>
                                <Globe size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8, color: '#818cf8' }} />
                                Personal Server Location
                            </h2>
                            <p className="wizard-team-subtitle" style={{ marginBottom: 32 }}>
                                Select the region where your Personal Space servers will be hosted for optimal latency.
                            </p>

                            <div className="grid grid-cols-2 gap-4 max-w-[600px] mx-auto text-left">
                                {SERVER_LOCATIONS.map((loc) => {
                                    const isSelected = selectedLocation === loc.id;
                                    return (
                                        <button
                                            key={loc.id}
                                            onClick={() => setSelectedLocation(loc.id)}
                                            className={cn(
                                                "w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all cursor-pointer relative overflow-hidden group",
                                                isSelected 
                                                    ? "border-indigo-500 shadow-lg shadow-indigo-500/20" 
                                                    : "border-slate-800 hover:border-slate-700 hover:shadow-md"
                                            )}
                                        >
                                            <div
                                                className={cn(
                                                    "absolute inset-0 bg-center transition-opacity duration-300",
                                                    isSelected ? "opacity-30" : "opacity-15 group-hover:opacity-25"
                                                )}
                                                style={{ 
                                                    backgroundImage: `url(/flags/${loc.cc}.png)`,
                                                    backgroundSize: '100% 100%'
                                                }}
                                            />
                                            <div className={cn(
                                                "absolute inset-0 transition-colors duration-200",
                                                isSelected
                                                    ? "bg-gradient-to-r from-indigo-950/80 via-slate-900/50 to-slate-900/30"
                                                    : "bg-gradient-to-r from-slate-900/90 via-slate-900/50 to-slate-900/20"
                                            )} />
                                            
                                            <div className="flex items-center gap-3 relative z-10">
                                                <span className="text-xl">{loc.flag}</span>
                                                <div className="flex flex-col text-left">
                                                    <span className={cn("text-sm font-bold leading-tight drop-shadow-sm", isSelected ? "text-indigo-300" : "text-white")}>
                                                        {loc.city}
                                                    </span>
                                                    <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{loc.region}</span>
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <Check size={18} className="text-indigo-500 relative z-10" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 2 && <WelcomeSteamStep />}

                    {step === 3 && (
                        <div className="wizard-slide" key={slideKey}>
                            <h2 className="wizard-team-title">How will you use StratSync?</h2>
                            <p className="wizard-team-subtitle">Choose a space to get started.</p>
                            <div className="wizard-team-options">
                                <button className="wizard-team-option" onClick={() => { setStep(4); setSlideKey(k => k + 1); }}>
                                    <div className="wizard-team-option-icon" style={{ background: 'rgba(99, 102, 241, 0.15)', color: '#818cf8' }}><Users size={28} /></div>
                                    <h3>I want to use StratSync with my team</h3>
                                    <p>Create your Faceit team as its owner, or join a team already created here.</p>
                                </button>
                                <button className="wizard-team-option" onClick={handleSkip}>
                                    <div className="wizard-team-option-icon" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa' }}><Crosshair size={28} /></div>
                                    <h3>I want to use StratSync solo</h3>
                                    <p>Continue to the main menu and enter your Personal Space.</p>
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="wizard-slide" key={slideKey}>
                            <h2 className="wizard-team-title">
                                <Sparkles size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8, color: '#fbbf24' }} />
                                Add Team
                            </h2>
                            <p className="wizard-team-subtitle">
                                Select a Faceit team to join or create to unlock all features.
                            </p>

                            <div className="mt-4 w-full flex-1 bg-slate-900/50 rounded-2xl border border-white/5 p-4 overflow-y-auto min-h-[360px]">
                                {loadingFaceitTeams ? (
                                    <div className="flex flex-col items-center justify-center h-full gap-3">
                                        <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                                        <p className="text-slate-500 text-sm font-semibold">Loading your Faceit teams...</p>
                                    </div>
                                ) : noFaceitAccount ? (
                                    <div className="flex flex-col items-center justify-center h-full gap-4">
                                        <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center">
                                            <AlertTriangle size={32} className="text-orange-500" />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-white font-bold text-sm mb-1">No Faceit Account Linked</p>
                                            <p className="text-slate-500 text-xs leading-relaxed max-w-xs mx-auto">
                                                Your account is not linked to a Faceit profile. Log in with Faceit first.
                                            </p>
                                        </div>
                                    </div>
                                ) : faceitTeams.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full gap-4">
                                        <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center">
                                            <Users size={32} className="text-slate-600" />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-white font-bold text-sm mb-1">No Faceit Teams Found</p>
                                            <p className="text-slate-500 text-xs leading-relaxed max-w-xs mx-auto">
                                                You are not a member of any Faceit team.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-4">
                                        {[...faceitTeams].sort((a: any, b: any) => {
                                            const aJoinable = a.alreadyExists && !a.alreadyJoined;
                                            const bJoinable = b.alreadyExists && !b.alreadyJoined;
                                            const aCreatable = !a.alreadyExists && a.isOwner;
                                            const bCreatable = !b.alreadyExists && b.isOwner;
                                            const rank = (creatable: boolean, joinable: boolean) => {
                                                if (joinable) return 1;
                                                if (creatable) return 2;
                                                return 3; // Member already
                                            };
                                            return rank(aCreatable, aJoinable) - rank(bCreatable, bJoinable);
                                        }).map((team: any) => {
                                            const isActualOwner = team.isOwner === true;
                                            const joinable = team.alreadyExists && !team.alreadyJoined;
                                            const creatable = !team.alreadyExists && isActualOwner;
                                            const isMember = team.alreadyExists && team.alreadyJoined;

                                            return (
                                                <div key={team.teamId} className={cn(
                                                    "w-full flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border transition-all cursor-default",
                                                    isMember ? "border-emerald-500/25 bg-emerald-500/5" :
                                                    joinable ? "border-slate-700 bg-slate-800/40" :
                                                    "border-slate-800 bg-slate-950/50 hover:border-blue-500/30"
                                                )}>
                                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                                        {team.avatar ? (
                                                            <img src={team.avatar} alt="" className={cn("shrink-0 w-10 h-10 rounded-xl object-cover border transition-all", team.alreadyExists ? "border-slate-700" : "border-slate-800")} />
                                                        ) : (
                                                            <div className={cn("shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm border", team.alreadyExists ? "bg-slate-800 text-slate-500 border-slate-700" : "bg-slate-800 text-slate-500 border-slate-700")}>
                                                                {team.name[0]}
                                                            </div>
                                                        )}
                                                        <div className="flex-1 text-left min-w-0">
                                                            <p className="font-bold text-[13px] truncate text-white">{team.name}</p>
                                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                                <p className="text-slate-500 text-[11px] font-semibold">{team.membersCount > 0 ? `${team.membersCount} members` : 'Faceit Team'}</p>
                                                                {joinable && (
                                                                    <p className="text-orange-400/90 text-[11px] italic font-semibold truncate">- Existing</p>
                                                                )}
                                                                {!team.alreadyExists && !isActualOwner && (
                                                                    <p className="text-slate-400 text-[10px] uppercase font-bold tracking-wider opacity-60 ml-1">Member</p>
                                                                )}
                                                                {creatable && isActualOwner && (
                                                                    <p className="text-amber-500 text-[10px] uppercase font-bold tracking-wider opacity-80 ml-1">Owner</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Action Buttons */}
                                                    <div className="flex items-center shrink-0">
                                                        {creatable && (
                                                            <button
                                                                onClick={() => onTeamAction(team.teamId, 'create', team)}
                                                                disabled={!!creatingTeamId}
                                                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-[11px] uppercase tracking-wider rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20 active:scale-95 flex items-center justify-center min-w-[70px]"
                                                            >
                                                                {creatingTeamId === team.teamId ? (
                                                                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                                ) : "Create"}
                                                            </button>
                                                        )}
                                                        {joinable && (
                                                            <button
                                                                onClick={() => onTeamAction(team.teamId, 'join', team)}
                                                                disabled={!!creatingTeamId}
                                                                className="px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white font-bold text-[11px] uppercase tracking-wider rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/20 active:scale-95 flex items-center justify-center min-w-[70px]"
                                                            >
                                                                {creatingTeamId === team.teamId ? (
                                                                    <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                                ) : "Join"}
                                                            </button>
                                                        )}
                                                        {isMember && <span className="text-emerald-400 text-[11px] font-bold uppercase">Already joined</span>}
                                                    </div>
                                                    {!team.alreadyExists && !isActualOwner && (
                                                        <p className="text-slate-400 text-[11px] mt-2 w-full">Only the owner of this Faceit team can create it in StratSync.</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {teamActionError && <p role="alert" className="text-red-400 text-sm font-semibold text-center mt-4">{teamActionError}</p>}
                            <div style={{ textAlign: 'center', marginTop: 24 }}>
                                <button
                                    className="wizard-btn wizard-btn-secondary"
                                    onClick={handleSkip}
                                    style={{ margin: '0 auto' }}
                                >
                                    Continue solo
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="wizard-footer">
                    <div className="wizard-dots">
                        {Array.from({ length: totalSteps }).map((_, i) => (
                            <div
                                key={i}
                                className={`wizard-dot ${i === step ? 'active' : ''}`}
                            />
                        ))}
                    </div>

                    {step === 4 && <button className="wizard-btn wizard-btn-secondary" onClick={() => { setStep(3); setSlideKey(k => k + 1); }}>Back</button>}
                    {step < 3 && (
                        <button className="wizard-btn wizard-btn-primary" onClick={handleNext}>
                            {step === 0 ? 'Get Started' : step === 2 ? 'Continue' : 'Next'}
                            <ArrowRight size={16} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
